/**
 * useAuth Hook — Canonical Authentication Hook
 * Provides authentication state and actions for login/logout operations,
 * plus standalone mutation hooks for password management, profile updates,
 * and session management.
 *
 * Combines Zustand auth store with React Query mutations.
 */

import { useCallback, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  useAuthStore,
  useCurrentUser,
  useIsAuthenticated,
  useAuthLoading,
  useAuthInitialized,
  useCurrentWorkspaceId,
} from '@/store/auth.store';
import * as authApi from '@/api/auth.api';
import { clearTokens, getAccessToken, getRefreshToken } from '@/utils/token';
import {
  assertSessionOwner,
  getSessionOwner,
  ownsSession,
  replaceSession,
  onSessionReplacement,
  ObsoleteSessionError,
} from '@/utils/session';
import { sanitizeReturnTo, DEFAULT_AFTER_LOGIN_PATH } from '@/utils/returnTo';
import { useSnackbar } from '@/components/feedback/Snackbar/useSnackbar';
import type { LoginFormData, RegisterFormData } from '@/features/auth/schemas';
import { parseApiError } from '@/features/auth/types';
import { PUBLIC_ROUTES } from '@/routes/routes';
import type {
  ForgotPasswordRequest,
  ResetPasswordRequest,
  ChangePasswordRequest,
  UpdateProfileRequest,
} from '@/types/api.types';

// Bind mutation retries and callbacks to the intent that existed before dispatch.
const mutationOwners = new WeakMap<object, number>();
const captureMutationOwner = (_variables: unknown, context: object) => {
  const owner = getSessionOwner();
  mutationOwners.set(context, owner);
  return { owner };
};
const captureAuthenticationIntent = (variables: unknown, context: object) => {
  replaceSession();
  return captureMutationOwner(variables, context);
};
const runSessionMutation = async <T>(context: object, operation: () => Promise<T>): Promise<T> => {
  const owner = mutationOwners.get(context)!;
  assertSessionOwner(owner);
  try {
    const result = await operation();
    assertSessionOwner(owner);
    return result;
  } catch (error) {
    assertSessionOwner(owner);
    throw error;
  }
};

// Re-export AuthError type for consumers
export type { AuthError } from '@/features/auth/types';

/**
 * Query key for current user cache seeding
 */
export const CURRENT_USER_QUERY_KEY = ['auth', 'current-user'];

/**
 * Query key for sessions
 */
export const SESSIONS_QUERY_KEY = ['auth', 'sessions'];

/**
 * useAuth Hook
 * Main hook for authentication operations
 *
 * @example
 * ```tsx
 * const { user, isAuthenticated, login, logout, isLoginLoading } = useAuth();
 *
 * const handleSubmit = async (data) => {
 *   try {
 *     await login(data);
 *     // User will be redirected to dashboard
 *   } catch (error) {
 *     // Error is available via loginError
 *   }
 * };
 * ```
 */
export const useAuth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  useEffect(() => onSessionReplacement(() => queryClient.clear()), [queryClient]);
  const { success: showSuccess, error: showError, info: showInfo } = useSnackbar();

  // Get state from store
  const user = useCurrentUser();
  const isAuthenticated = useIsAuthenticated();
  const isLoading = useAuthLoading();
  const isInitialized = useAuthInitialized();
  const currentWorkspaceId = useCurrentWorkspaceId();

  // Get actions from store
  const { setAuth, setUser, logout: storeLogout } = useAuthStore();

  const completeMcpGatewayLoginHandoff = useCallback(
    async (gatewayState: string, workspaceId?: number) => {
      const owner = getSessionOwner();
      const handoff = await authApi.createMcpGatewayLoginHandoff({
        gatewayState,
        workspaceId,
      });
      assertSessionOwner(owner);
      window.location.href = handoff.callbackUrl;
    },
    []
  );

  /**
   * Login mutation
   */
  const loginMutation = useMutation({
    onMutate: captureAuthenticationIntent,
    mutationFn: async (credentials: LoginFormData, context) => {
      const response = await runSessionMutation(context, () =>
        authApi.login({
          email: credentials.email,
          password: credentials.password,
        })
      );
      // Return both API response and rememberMe preference
      return { ...response, rememberMe: credentials.rememberMe };
    },
    onSuccess: async (data, _variables, intent) => {
      assertSessionOwner(intent!.owner);
      // Update auth store with user, tokens, and rememberMe preference
      setAuth(
        data.user,
        {
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
        },
        data.rememberMe
      );

      const owner = getSessionOwner();
      // Seed the query cache with user data
      queryClient.setQueryData(CURRENT_USER_QUERY_KEY, data.user);

      if (data.user.mustChangePassword) {
        navigate(PUBLIC_ROUTES.CHANGE_PASSWORD, { replace: true });
        return;
      }

      const gatewayState = searchParams.get('gateway_state');
      if (searchParams.has('gateway_state')) {
        await completeMcpGatewayLoginHandoff(
          gatewayState ?? '',
          useAuthStore.getState().currentWorkspaceId ?? data.user.defaultWorkspaceId ?? undefined
        );
        return;
      }

      // Show success notification
      showSuccess(`Welcome back, ${data.user.firstName}!`);

      // Redirect precedence (per auth-fix-4 spec):
      //   1. valid ?returnTo= query param (survives hard reloads, which lose state)
      //   2. React Router location.state.from (guest-route redirect path)
      //   3. DEFAULT_AFTER_LOGIN_PATH (/dashboard)
      const sanitizedReturnTo = sanitizeReturnTo(searchParams.get('returnTo'));
      const fromLocation = (
        location.state as { from?: { pathname: string; search?: string; hash?: string } }
      )?.from;
      const fromStateUrl = fromLocation
        ? `${fromLocation.pathname}${fromLocation.search || ''}${fromLocation.hash || ''}`
        : null;
      const destination =
        sanitizedReturnTo ??
        sanitizeReturnTo(fromStateUrl) ??
        (data.user.platformRole === 'Super Admin' ? '/admin/beta' : DEFAULT_AFTER_LOGIN_PATH);
      navigate(destination, { replace: true });

      // Fetch full user profile in background (auth response only has partial user)
      try {
        const fullUser = await authApi.getCurrentUser();
        if (!ownsSession(owner)) return;
        setUser(fullUser);
        queryClient.setQueryData(CURRENT_USER_QUERY_KEY, fullUser);
      } catch {
        // Graceful degradation: partial user is acceptable short-term
        console.warn('Failed to fetch full user profile after login');
      }
    },
    onError: (error) => {
      if (error instanceof ObsoleteSessionError) return;
      showError('Login failed', {
        description: 'Please check your email and password',
      });
    },
  });

  /**
   * Register mutation
   */
  const registerMutation = useMutation({
    onMutate: captureAuthenticationIntent,
    mutationFn: async (data: RegisterFormData, context) => {
      const response = await runSessionMutation(context, () =>
        authApi.register({
          email: data.email,
          password: data.password,
          firstName: data.firstName,
          lastName: data.lastName,
        })
      );
      return response;
    },
    onSuccess: async (data, _variables, intent) => {
      assertSessionOwner(intent!.owner);
      setAuth(
        data.user,
        {
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
        },
        false
      );

      const owner = getSessionOwner();
      // Seed the query cache with user data
      queryClient.setQueryData(CURRENT_USER_QUERY_KEY, data.user);

      // Show success notification
      showSuccess(`Welcome, ${data.user.firstName}!`, {
        description: 'Your account has been created successfully',
      });

      const sanitizedReturnTo = sanitizeReturnTo(searchParams.get('returnTo'));
      navigate(sanitizedReturnTo ?? DEFAULT_AFTER_LOGIN_PATH, { replace: true });

      // Fetch full user profile in background (auth response only has partial user)
      try {
        const fullUser = await authApi.getCurrentUser();
        if (!ownsSession(owner)) return;
        setUser(fullUser);
        queryClient.setQueryData(CURRENT_USER_QUERY_KEY, fullUser);
      } catch {
        // Graceful degradation: partial user is acceptable short-term
        console.warn('Failed to fetch full user profile after register');
      }
    },
    onError: (error) => {
      if (error instanceof ObsoleteSessionError) return;
      showError('Registration failed', {
        description: 'Please check your information and try again',
      });
    },
  });

  /**
   * Logout mutation
   */
  const logoutMutation = useMutation({
    mutationFn: async ({
      refreshToken,
      accessToken,
    }: {
      refreshToken?: string;
      accessToken: string;
    }) => {
      try {
        await authApi.logout(refreshToken, accessToken);
      } catch {
        // Local termination is final even if revocation is offline or obsolete.
      }
    },
  });

  /**
   * Login function
   */
  const login = useCallback(
    async (credentials: LoginFormData) => {
      return loginMutation.mutateAsync(credentials);
    },
    [loginMutation]
  );

  /**
   * Register function
   */
  const register = useCallback(
    async (data: RegisterFormData) => {
      return registerMutation.mutateAsync(data);
    },
    [registerMutation]
  );

  /**
   * Logout function
   */
  const logout = useCallback(async () => {
    const refreshToken = getRefreshToken() ?? undefined;
    const accessToken = getAccessToken() ?? '';
    storeLogout();
    queryClient.clear();
    showInfo('You have been logged out');
    navigate('/login', { replace: true });
    return logoutMutation.mutateAsync({ refreshToken, accessToken });
  }, [logoutMutation, storeLogout, queryClient, showInfo, navigate]);

  return {
    // State
    user,
    isAuthenticated,
    isLoading,
    isInitialized,
    currentWorkspaceId,

    // Login
    login,
    isLoginLoading: loginMutation.isPending,
    loginError: loginMutation.error ? parseApiError(loginMutation.error) : null,
    resetLoginError: loginMutation.reset,
    completeMcpGatewayLoginHandoff,

    // Register
    register,
    isRegisterLoading: registerMutation.isPending,
    registerError: registerMutation.error ? parseApiError(registerMutation.error) : null,
    resetRegisterError: registerMutation.reset,

    // Logout
    logout,
    isLogoutLoading: logoutMutation.isPending,
  };
};

/**
 * useAuthState hook
 * Lightweight hook for components that only need auth state (no actions)
 */
export const useAuthState = () => {
  const user = useCurrentUser();
  const isAuthenticated = useIsAuthenticated();
  const isLoading = useAuthLoading();
  const isInitialized = useAuthInitialized();

  return {
    user,
    isAuthenticated,
    isLoading,
    isInitialized,
  };
};

// ---------------------------------------------------------------------------
// Standalone mutation / query hooks (moved from api/hooks/useAuth.ts)
// ---------------------------------------------------------------------------

/**
 * Hook to fetch fresh user profile via React Query.
 * Use this on pages that need loading/error states for the user fetch
 * (e.g., Profile page). For synchronous access, use useCurrentUser from
 * the auth store instead.
 */
export const useProfileQuery = () => {
  const isAuthenticated = useIsAuthenticated();

  return useQuery({
    queryKey: CURRENT_USER_QUERY_KEY,
    queryFn: async () => {
      const owner = getSessionOwner();
      const user = await authApi.getCurrentUser();
      assertSessionOwner(owner);
      return user;
    },
    enabled: isAuthenticated,
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: false,
  });
};

/**
 * Hook to request password reset
 */
export const useForgotPassword = () => {
  const { success, error } = useSnackbar();

  return useMutation({
    mutationFn: (data: ForgotPasswordRequest) => authApi.forgotPassword(data),
    onSuccess: () => {
      success('Password reset email sent', {
        description: 'Please check your email for reset instructions',
        duration: 10000,
      });
    },
    onError: () => {
      error('Failed to send reset email', {
        description: 'Please try again or contact support',
      });
    },
  });
};

/**
 * Hook to reset password
 */
export const useResetPassword = () => {
  const { success, error } = useSnackbar();

  return useMutation({
    mutationFn: (data: ResetPasswordRequest) => authApi.resetPassword(data),
    onSuccess: () => {
      success('Password reset successful', {
        description: 'You can now login with your new password',
      });
    },
    onError: () => {
      error('Password reset failed', {
        description: 'The reset link may be invalid or expired',
      });
    },
  });
};

/**
 * Hook to change password
 */
export const useChangePassword = () => {
  const { success, error } = useSnackbar();

  return useMutation({
    mutationFn: (data: ChangePasswordRequest) => authApi.changePassword(data),
    onSuccess: () => {
      success('Password changed successfully');
    },
    onError: () => {
      error('Failed to change password', {
        description: 'Please check your current password',
      });
    },
  });
};

/**
 * Hook to update profile
 */
export const useUpdateProfile = () => {
  const setUser = useAuthStore((state) => state.setUser);
  const { success, error } = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation({
    onMutate: captureMutationOwner,
    mutationFn: (data: UpdateProfileRequest, context) =>
      runSessionMutation(context, () => authApi.updateProfile(data)),
    onSuccess: (user, _variables, intent) => {
      assertSessionOwner(intent!.owner);
      // updateProfile returns User directly (not wrapped in {user: ...})
      setUser(user);
      queryClient.setQueryData(CURRENT_USER_QUERY_KEY, user);
      success('Profile updated successfully');
    },
    onError: (failure) => {
      if (failure instanceof ObsoleteSessionError) return;
      error('Failed to update profile');
    },
  });
};

/**
 * Hook to get active sessions
 */
export const useSessions = () => {
  const isAuthenticated = useIsAuthenticated();

  return useQuery({
    queryKey: SESSIONS_QUERY_KEY,
    queryFn: async () => {
      const owner = getSessionOwner();
      const sessions = await authApi.getSessions();
      assertSessionOwner(owner);
      return sessions;
    },
    enabled: isAuthenticated,
    staleTime: 60 * 1000, // 1 minute
  });
};

/**
 * Hook to revoke all sessions
 */
export const useRevokeAllSessions = () => {
  const logoutStore = useAuthStore((state) => state.logout);
  const { success, error } = useSnackbar();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    onMutate: captureMutationOwner,
    mutationFn: (_variables: void, context) =>
      runSessionMutation(context, authApi.revokeAllSessions),
    onSuccess: (_result, _variables, intent) => {
      assertSessionOwner(intent!.owner);
      success('All sessions revoked');
      clearTokens();
      logoutStore();
      queryClient.clear();
      navigate('/login', { replace: true });
    },
    onError: (failure) => {
      if (failure instanceof ObsoleteSessionError) return;
      error('Failed to revoke sessions');
    },
  });
};
